import { useMemo, type ReactNode } from "react";
import { Outlet, useParams } from "react-router-dom";
import { AppShell } from "@/components/shell/AppShell";
import { todoBadges, useAdminTodo } from "@/features/admin-todo";
import { useApp } from "@/hooks/use-app-context";
import { buildAdminNav } from "./admin-nav";
import { AdminScopeNotice } from "./AdminScopeNotice";
import { contributionNavigation } from "@/components/shell/contribution-navigation";

export function AdminLayout({ children }: { children?: ReactNode } = {}) {
  const { lang = "fr" } = useParams<{ lang: string }>();
  const { t, user } = useApp();
  // Same queries as the dashboard's "to do" row: the cache is shared.
  const { counts } = useAdminTodo();
  const { payments, tests, translations, complaints, phraseFiles } = counts;
  const badges = useMemo(
    () => todoBadges({ payments, tests, translations, complaints, phraseFiles }),
    [payments, tests, translations, complaints, phraseFiles],
  );
  const sections = useMemo(
    () => [...buildAdminNav(lang, t, user?.role, badges), contributionNavigation(lang, t.contribute)],
    [lang, t, user?.role, badges],
  );
  const roles = t.admin.layout.roles as Record<string, string>;

  return (
    <AppShell
      title={lang === "en" ? "Administration" : "Administration"}
      homeHref={`/${lang}/admin/dashboard`}
      settingsHref={`/${lang}/admin/profile`}
      sections={sections}
      roleLabel={roles[user?.role ?? ""] ?? user?.role}
    >
      <div className="space-y-6">
        <AdminScopeNotice />
        {children ?? <Outlet />}
      </div>
    </AppShell>
  );
}
